// ../components/MapChart.js
import React, { useState, useEffect } from 'react';
import { Chart } from "react-google-charts";
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from "@react-keycloak/web";

const MapChart = ({ onSelect }) => {
    const [chartData, setChartData] = useState([
        ['Country', 'Value', { role: 'tooltip', type: 'string', p: { html: true } }],
        ['Greece', 1, ''],
        ['Italy', 1, ''],
        ['Denmark', 1, ''],
        ['Austria', 1, ''],
        ['Ireland', 1, ''],
        ['Spain', 1, ''],
        ['Romania', 1, '']
    ]);
    
    const [chartError, setChartError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { keycloak, initialized } = useKeycloak();

    const { palette: { secondary: { main: secondaryColor } } } = useTheme();

    // Reset chart state when authentication changes
    useEffect(() => {
        if (initialized) {
            setIsLoading(false);
            setChartError(null);
        }
    }, [initialized, keycloak.authenticated]);

    const options = {
        colorAxis: { values: [1, 2], colors: ['#4285F4', secondaryColor] },
        datalessRegionColor: '#F0F0F0',
        defaultColor: '#4285F4',
        legend: 'none',
        region: '150',
        tooltip: { isHtml: true },
        keepAspectRatio: false, // Allow map to fill the container
        backgroundColor: '#FFFFFF',
        borderColor: '#000000',
        borderWidth: 2,
        enableRegionInteractivity: true,
    };

    const chartEvents = [
        {
            eventName: 'select',
            callback: ({ chartWrapper }) => {
                try {
                    const chart = chartWrapper.getChart();
                    const selection = chart.getSelection();
                    if (selection.length === 0) return;
                    const dataTable = chartWrapper.getDataTable();
                    const { row } = selection[0];
                    const country = dataTable.getValue(row, 0);
                    onSelect(country); // Pass selected country back to the parent
                    setChartData(prevData =>
                        prevData.map(([countryName, value, tooltip]) =>
                            countryName === 'Country' ? [countryName, value, tooltip] : [countryName, countryName === country ? 2 : 1, tooltip]
                        )
                    );
                } catch (error) {
                    console.error('Error in chart selection:', error);
                    setChartError('Failed to select country. Please try again.');
                }
            },
        },
        {
            eventName: 'error',
            callback: (error) => {
                console.error('Google Charts error:', error);
                setChartError('Failed to load map. Please refresh the page and try again.');
                setIsLoading(false);
            },
        },
        {
            eventName: 'ready',
            callback: () => {
                setIsLoading(false);
                setChartError(null);
            },
        },
    ];

    if (!initialized || isLoading) {
        return (
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: theme => theme.shape.borderRadius,
                overflow: 'hidden',
            }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading map...</Typography>
            </Box>
        );
    }

    if (chartError) {
        return (
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: theme => theme.shape.borderRadius,
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
            }}>
                <Typography color="error" textAlign="center">
                    {chartError}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            borderRadius: theme => theme.shape.borderRadius,
            overflow: 'hidden',
        }}>
            <Chart
                width={'100%'}
                height={'100%'}
                chartType="GeoChart"
                chartVersion="51"
                data={chartData}
                options={options}
                chartEvents={chartEvents}
            />
        </Box>
    );
};

export default MapChart;
